<template>
  <div class="stepper-accordion-v">
    <!-- Header section -->
    <div class="stepper-accordion-v__header" v-if="title">
      <v-btn
        icon
        class="stepper-accordion-v__back-btn"
        @click="$emit('back')"
        v-if="showBackButton"
      >
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <!-- <h3 class="stepper-accordion-v__title">{{ title }}</h3> -->
    </div>

    <!-- Responsive Grid Container -->
    <v-container fluid class="stepper-accordion-v__container px-0 overflow-hidden">
      <v-row justify="center">
        <v-col
          cols="12"
          md="12"
          lg="12"
          xl="8"
          class="stepper-accordion-v__col"
        >
          <!-- Vuetify V-Stepper in vertical mode -->
          <v-stepper
            v-model="currentStep"
            vertical
            class="stepper-accordion-v__stepper"
            elevation="0"
          >
      <template v-for="(step, index) in steps">
        <v-stepper-step
          :key="`step-${index}`"
          :step="index + 1"
          :complete="isStepComplete(index)"
          :editable="!step.disabled"
          :class="{ 'stepper-accordion-v__step--disabled': step.disabled }"
          :color="stepperColors.primary"
          @click="handleStepClick(index)"
        >
          <div class="stepper-accordion-v__step-label">
            <h3 class="stepper-accordion-v__step-title">{{ step.title }}</h3>
            <div class="stepper-accordion-v__step-subtitle" v-if="step.subtitle">{{ step.subtitle }}</div>
            <div class="stepper-accordion-v__disabled-reason" v-if="step.disabled && step.disabledReason">{{ step.disabledReason }}</div>
          </div>
          <div class="stepper-accordion-v__step-check" v-if="isStepComplete(index)">
            <v-icon color="#16a34a" size="24">mdi-check</v-icon>
          </div>
        </v-stepper-step>

        <v-stepper-content
          :key="`content-${index}`"
          :step="index + 1"
          class="stepper-accordion-v__content"
        >
          <div class="stepper-accordion-v__step-content">
            <slot :name="`step-${index}`" :step="step" :index="index">
              <div class="stepper-accordion-v__default-content">
                {{ step.content }}
              </div>
            </slot>
          </div>
        </v-stepper-content>
      </template>
          </v-stepper>
        </v-col>
      </v-row>
    </v-container>

    <!-- Actions section -->
    <div class="stepper-accordion-v__actions" v-if="showActions">
      <v-btn
        outlined
        class="stepper-accordion-v__action-btn"
        :disabled="!canGoPrevious"
        @click="$emit('previous')"
      >
        Previous
      </v-btn>
      <v-btn
        class="stepper-accordion-v__action-btn stepper-accordion-v__action-btn--primary"
        :disabled="!canGoNext"
        @click="$emit('next')"
      >
        Next
      </v-btn>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import type { PropType } from 'vue'

export interface StepperStep {
  title: string;
  subtitle?: string;
  content?: string;
  disabled?: boolean;
  disabledReason?: string;
  stepComplete?: boolean;
}

type StepperData = {
  currentStep: number;
  completedSteps: number[];
}

type StepperMethods = {
  handleStepClick(index: number): void;
  isStepComplete(index: number): boolean;
  markStepComplete(index: number): void;
  previousStep(): void;
  nextStep(): void;
  scrollToActiveStep(): void;
}

type StepperComputed = {
  activeStepData: StepperStep | null;
  stepperColors: { primary: string };
}

type StepperProps = {
  steps: StepperStep[];
  title: string;
  showBackButton: boolean;
  showActions: boolean;
  canGoPrevious: boolean;
  canGoNext: boolean;
  initialStep: number;
  allowMultipleActive: boolean;
  currentStepIndex: number;
}

/**
 * StepperAccordion provides a vertical stepper interface using Vuetify's v-stepper component.
 * Based on the Risk Hub design pattern, it displays numbered steps that can be expanded
 * to show content. Only one step can be expanded at a time.
 */
export default Vue.extend<StepperData, StepperMethods, StepperComputed, StepperProps>({
  name: 'StepperAccordion',
  props: {
    /** Array of step objects with title, subtitle, and content */
    steps: {
  type: Array as PropType<StepperStep[]>,
      required: true
    },
    /** Main title displayed at the top of the component */
    title: {
      type: String,
      default: ''
    },
    /** Whether to show the back button in the header */
    showBackButton: {
      type: Boolean,
      default: false
    },
    /** Whether to show the Previous/Next action buttons */
    showActions: {
      type: Boolean,
      default: false
    },
    /** Whether the Previous button should be enabled */
    canGoPrevious: {
      type: Boolean,
      default: true
    },
    /** Whether the Next button should be enabled */
    canGoNext: {
      type: Boolean,
      default: true
    },
    /** Index of the initially active step */
    initialStep: {
      type: Number,
      default: 1
    },
    /** Allow multiple steps to be completed/active */
    allowMultipleActive: {
      type: Boolean,
      default: false
    },
    /** Current active step index (0-based) - used for external step management */
    currentStepIndex: {
      type: Number,
      default: 0
    }
  },
  data(): StepperData {
    return {
      currentStep: this.initialStep,
      completedSteps: []
    };
  },
  computed: {
    /**
     * Returns the currently active step object
     * @returns {StepperStep|null} The current step or null if no step is active
     */
  activeStepData(): StepperStep | null {
      return this.currentStep > 0 ? this.steps[this.currentStep - 1] : null;
    },
    /**
     * Color configuration for the stepper
     */
  stepperColors(): { primary: string } {
      return {
        primary: '#406A94'
      };
    }
  },
  watch: {
  initialStep(newVal: number) {
      this.currentStep = newVal;
    },
  currentStepIndex(newVal: number) {
      this.currentStep = newVal + 1; // Convert to 1-based for v-stepper
    },
  currentStep(newVal: number) {
      this.$emit('step-change', newVal); // Emit 1-based index to match current behavior
      this.$nextTick(() => {
        this.scrollToActiveStep();
      });
    }
  },
  methods: {
    /**
     * Handles step click events
     * @param {number} index - The index of the step that was clicked (0-based)
     */
  handleStepClick(index: number): void {
      if (this.steps[index]?.disabled) return;

      const stepNumber = index + 1; // Convert to 1-based for v-stepper
      this.currentStep = stepNumber;
      // Scroll functionality will be triggered by the currentStep watcher
    },
    /**
     * Checks if a step is complete
     * @param {number} index - The step index (0-based)
     */
  isStepComplete(index: number): boolean {
      // Disabled steps should never appear as complete (prevents green styling)
      if (this.steps[index]?.disabled) return false;
      // Check if step has explicit completion status, otherwise use internal tracking
      if (this.steps[index] && typeof this.steps[index].stepComplete === 'boolean') {
        return !!this.steps[index].stepComplete;
      }
      return this.completedSteps.includes(index);
    },
    /**
     * Marks a step as complete
     * @param {number} index - The step index (0-based)
     */
  markStepComplete(index: number): void {
      if (!this.completedSteps.includes(index)) {
        this.completedSteps.push(index);
      }
    },
    /**
     * Goes to the previous step
     */
  previousStep(): void {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },
    /**
     * Goes to the next step
     */
  nextStep(): void {
      if (this.currentStep < this.steps.length) {
        this.markStepComplete(this.currentStep - 1); // Mark current as complete
        this.currentStep++;
      }
    },
    /**
     * Scrolls the active step to the top of the viewport
     */
  scrollToActiveStep(): void {
      // Find the active step element by looking for the step with the current step number
      const activeStepElement = this.$el.querySelector(`.v-stepper__step--active`);
      if (activeStepElement) {
        // Calculate the scroll position to bring the active step to the top of the viewport
        const elementTop = activeStepElement.getBoundingClientRect().top + window.pageYOffset;
        const headerOffset = 80; // Account for any fixed headers or top padding

        window.scrollTo({
          top: Math.max(0, elementTop - headerOffset),
          behavior: 'smooth'
        });
      }
    }
  }
});
</script>

<style lang="scss" scoped>
.stepper-accordion-v {
  width: 100%;
  background: var(--bg-secondary);

  &__container {
    padding: 16px;
    padding-bottom: 24px; // Reduced space for fixed actions
  }

  &__col {
    padding: 0; // Remove default v-col padding since we control it with container
  }

  &__header {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
  }

  &__back-btn {
    margin-right: 16px;
    color: #406A94 !important;
  }

  &__title {
    font-size: 24px;
    font-weight: 500;
    color: #3C3C3C;
    margin: 0;
  }

  // V-Stepper styling with brand colors
  &__stepper {
    // Grey out non-editable (disabled) steps
    ::v-deep .v-stepper__step:not(.v-stepper__step--editable) .v-stepper__step__step {
      background: #e0e0e0 !important;
      color: #9e9e9e !important;
    }
    ::v-deep .v-stepper__step:not(.v-stepper__step--editable) .v-stepper__label,
    ::v-deep .v-stepper__step:not(.v-stepper__step--editable) .v-stepper__label .stepper-accordion-v__step-title,
    ::v-deep .v-stepper__step:not(.v-stepper__step--editable) .v-stepper__label .stepper-accordion-v__step-subtitle {
      color: #9e9e9e !important;
    }
    background: transparent !important;
    box-shadow: none !important;

    // Style the step icons with brand colors
    ::v-deep .v-stepper__step__step {
      background: #406A94 !important;
      color: #ffffff !important;
      border: none !important;
      width: 32px !important;
      height: 32px !important;
      min-width: 32px !important;
      min-height: 32px !important;
      border-radius: 50% !important;
      font-size: 16px !important;
      font-weight: 600 !important;
    }

    // Style completed steps - keep numbers visible, hide default icons
    ::v-deep .v-stepper__step--complete .v-stepper__step__step {
      background: #16a34a !important; // Success green for completed
    }

    // Override Vuetify's completed step icons with step numbers
    ::v-deep .v-stepper__step--complete .v-stepper__step__step .v-icon {
      display: none !important;
    }

    // Force completed steps to show step numbers by overriding Vuetify's content
    ::v-deep .v-stepper__step--complete .v-stepper__step__step::before {
      content: "1" !important;
      position: absolute !important;
      top: 50% !important;
      left: 50% !important;
      transform: translate(-50%, -50%) !important;
      font-size: 16px !important;
      font-weight: 600 !important;
      color: #ffffff !important;
      z-index: 2 !important;
    }

    // Specific overrides for each step number
    ::v-deep .v-stepper__step:nth-child(1).v-stepper__step--complete .v-stepper__step__step::before {
      content: "1" !important;
    }
    ::v-deep .v-stepper__step:nth-child(3).v-stepper__step--complete .v-stepper__step__step::before {
      content: "2" !important;
    }
    ::v-deep .v-stepper__step:nth-child(5).v-stepper__step--complete .v-stepper__step__step::before {
      content: "3" !important;
    }
    ::v-deep .v-stepper__step:nth-child(7).v-stepper__step--complete .v-stepper__step__step::before {
      content: "4" !important;
    }
    ::v-deep .v-stepper__step:nth-child(9).v-stepper__step--complete .v-stepper__step__step::before {
      content: "5" !important;
    }
    ::v-deep .v-stepper__step:nth-child(11).v-stepper__step--complete .v-stepper__step__step::before {
      content: "6" !important;
    }
    ::v-deep .v-stepper__step:nth-child(13).v-stepper__step--complete .v-stepper__step__step::before {
      content: "7" !important;
    }
    ::v-deep .v-stepper__step:nth-child(15).v-stepper__step--complete .v-stepper__step__step::before {
      content: "8" !important;
    }
    ::v-deep .v-stepper__step:nth-child(17).v-stepper__step--complete .v-stepper__step__step::before {
      content: "9" !important;
    }
    ::v-deep .v-stepper__step:nth-child(19).v-stepper__step--complete .v-stepper__step__step::before {
      content: "10" !important;
    }

    // Ensure completed steps maintain proper styling
    ::v-deep .v-stepper__step--complete .v-stepper__step__step {
      position: relative !important;
      background: #16a34a !important;
      width: 32px !important;
      height: 32px !important;
      min-width: 32px !important;
      min-height: 32px !important;
      border-radius: 50% !important;
      font-size: 16px !important;
      font-weight: 600 !important;
      color: #ffffff !important;
    }

    // Ensure default step numbers are visible and properly styled
    ::v-deep .v-stepper__step__step {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      color: #ffffff !important;
      font-size: 16px !important;
      font-weight: 600 !important;
    }

    // Style active step
    ::v-deep .v-stepper__step--active .v-stepper__step__step {
      background: #406A94 !important;
      width: 32px !important;
      height: 32px !important;
      min-width: 32px !important;
      min-height: 32px !important;
      border-radius: 50% !important;
      box-shadow: none !important;
    }

    // Create a border around each complete step (header + content as one unit)
    ::v-deep .v-stepper__step {
      padding: 16px 24px !important;
      background: var(--bg-secondary);
      border: 1px solid #e5e7eb; // Custom border color (closest token: --brand-neutral-medium-border #e0e0e0)
      border-radius: var(--radius); // Full radius for collapsed steps
      margin-bottom: 16px;
      margin-left: 16px;
      margin-right: 16px;
    }

    // Active step header has different border radius when content is shown
    ::v-deep .v-stepper__step--active {
      border-bottom: none; // Will be completed by content
      border-radius: var(--radius) var(--radius) 0 0;
      margin-bottom: 0;
    }

    // Style step content to complete the border
    ::v-deep .v-stepper__content {
      padding: 0 24px 24px 24px !important;
      background: var(--bg-secondary);
      margin-left: 16px;
      margin-right: 16px;
      border: 1px solid #e5e7eb; // Custom border color (closest token: --brand-neutral-medium-border #e0e0e0)
      border-top: none; // Connect seamlessly with header
      border-radius: 0 0 var(--radius) var(--radius);
      margin-bottom: 16px;
      margin-top: 0;
      display: none; // Hide by default (accordion style)
    }

    // Show content only for active step
    ::v-deep .v-stepper__step--active + .v-stepper__content {
      display: block !important;
    }

    // Additional styling for active step border color
    ::v-deep .v-stepper__step--active {
      border-color: #e5e7eb !important;

      + .v-stepper__content {
        border-color: #e5e7eb !important;
      }
    }

    // Remove hover states from stepper heads
    ::v-deep .v-stepper__step:hover {
      background: var(--bg-secondary) !important;
      border-color: #e5e7eb !important;
      cursor: default !important;
    }

    ::v-deep .v-stepper__step .v-stepper__label:hover {
      background: transparent !important;
    }

    ::v-deep .v-stepper__step .v-stepper__step__step:hover {
      background: #406A94 !important;
      transform: none !important;
      box-shadow: none !important;
    }

    // Remove hover states from completed steps
    ::v-deep .v-stepper__step--complete:hover .v-stepper__step__step {
      background: #16a34a !important;
      transform: none !important;
      box-shadow: none !important;
    }

    // Remove hover states from active steps
    ::v-deep .v-stepper__step--active:hover .v-stepper__step__step {
      background: #406A94 !important;
      box-shadow: none !important;
      transform: none !important;
    }
    
    // Remove visual effects on active/focus but maintain click functionality
    ::v-deep .v-stepper__step:active,
    ::v-deep .v-stepper__step:focus {
      outline: none !important;
    }
    
    ::v-deep .v-stepper__step .v-stepper__step__step:active,
    ::v-deep .v-stepper__step .v-stepper__step__step:focus {
      transform: none !important;
      box-shadow: inherit !important;
      outline: none !important;
    }
    
    // Keep step number circles non-interactive visually but allow clicks to propagate
    ::v-deep .v-stepper__step .v-stepper__step__step {
      transition: none !important;
    }
    
    // Remove ripple effect but keep click functionality
    ::v-deep .v-stepper__step .v-ripple__container {
      display: none !important;
    }
    
    // Override focus outline
    ::v-deep .v-stepper__step:focus {
      outline: none !important;
    }
    
    // Maintain cursor pointer for clickable steps
    ::v-deep .v-stepper__step {
      cursor: pointer !important;
    }

    // Style step labels
    ::v-deep .v-stepper__label {
      text-align: left !important;
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;
      width: 100% !important;
    }

    // Disabled step visuals (greyed out number and label)
    ::v-deep .stepper-accordion-v__step--disabled {
      cursor: not-allowed !important;
    }
    ::v-deep .stepper-accordion-v__step--disabled .v-stepper__step__step {
      background: #e0e0e0 !important;
      color: #9e9e9e !important;
    }
    // Prevent any override from completed state accidentally applying green
    ::v-deep .stepper-accordion-v__step--disabled.v-stepper__step--complete .v-stepper__step__step,
    ::v-deep .stepper-accordion-v__step--disabled .v-stepper__step__step::before {
      background: #e0e0e0 !important;
      color: #9e9e9e !important;
      content: attr(data-step) !important; /* fallback to numeric if before content injected elsewhere */
    }
    ::v-deep .stepper-accordion-v__step--disabled .v-stepper__label,
    ::v-deep .stepper-accordion-v__step--disabled .v-stepper__label .stepper-accordion-v__step-title,
    ::v-deep .stepper-accordion-v__step--disabled .v-stepper__label .stepper-accordion-v__step-subtitle {
      color: #9e9e9e !important;
    }

    // Remove hover effects for disabled steps (keep visuals static/grey)
    ::v-deep .stepper-accordion-v__step--disabled:hover,
    ::v-deep .stepper-accordion-v__step--disabled:focus,
    ::v-deep .stepper-accordion-v__step--disabled:active {
      background: var(--bg-secondary) !important;
      border-color: #e5e7eb !important;
    }
    ::v-deep .stepper-accordion-v__step--disabled .v-stepper__step__step:hover,
    ::v-deep .stepper-accordion-v__step--disabled .v-stepper__step__step:focus,
    ::v-deep .stepper-accordion-v__step--disabled .v-stepper__step__step:active {
      background: #e0e0e0 !important;
      box-shadow: none !important;
      transform: none !important;
    }
    ::v-deep .stepper-accordion-v__step--disabled .v-stepper__label:hover {
      background: transparent !important;
    }

    // Style cards within step content to match Risk Hub and step borders
    ::v-deep .v-stepper__content .v-card {
      background: rgb(240,241,242) !important;
      border: 1px solid #e5e7eb !important; // Match step border color
      border-radius: var(--radius-lg) !important; // Increased radius for cards
      box-shadow: none !important; // Remove drop shadow
    }

    // Remove border between card header and body
    ::v-deep .v-stepper__content .v-card .v-card__title,
    ::v-deep .v-stepper__content .v-card .v-card__subtitle,
    ::v-deep .v-stepper__content .v-card .v-card__header {
      border-bottom: none !important;
    }

    ::v-deep .v-stepper__content .v-card .v-card__title + .v-card__text,
    ::v-deep .v-stepper__content .v-card .v-card__title + .v-card__actions,
    ::v-deep .v-stepper__content .v-card .v-card__subtitle + .v-card__text,
    ::v-deep .v-stepper__content .v-card .v-card__subtitle + .v-card__actions {
      border-top: none !important;
    }

    // Remove HR separators from cards
    ::v-deep .v-stepper__content .v-card hr,
    ::v-deep .v-stepper__content .v-card .v-divider,
    ::v-deep .v-stepper__content .card hr,
    ::v-deep .v-stepper__content .form-card hr,
    ::v-deep .v-stepper__content .content-card hr {
      display: none !important;
    }

    // Match text field and select menu borders to card border color with subtle elevation
    ::v-deep .v-stepper__content .v-card .v-text-field .v-input__control .v-input__slot,
    ::v-deep .v-stepper__content .v-card .v-select .v-input__control .v-input__slot,
    ::v-deep .v-stepper__content .v-card .v-textarea .v-input__control .v-input__slot,
    ::v-deep .v-stepper__content .card .v-text-field .v-input__control .v-input__slot,
    ::v-deep .v-stepper__content .card .v-select .v-input__control .v-input__slot,
    ::v-deep .v-stepper__content .card .v-textarea .v-input__control .v-input__slot,
    ::v-deep .v-stepper__content .form-card .v-text-field .v-input__control .v-input__slot,
    ::v-deep .v-stepper__content .form-card .v-select .v-input__control .v-input__slot,
    ::v-deep .v-stepper__content .form-card .v-textarea .v-input__control .v-input__slot,
    ::v-deep .v-stepper__content .content-card .v-text-field .v-input__control .v-input__slot,
    ::v-deep .v-stepper__content .content-card .v-select .v-input__control .v-input__slot,
    ::v-deep .v-stepper__content .content-card .v-textarea .v-input__control .v-input__slot {
      border-color: #e5e7eb !important;
			background: rgb(240,241,242) !important;
    }

    // Also target outlined variants with elevation
    ::v-deep .v-stepper__content .v-card .v-text-field--outlined fieldset,
    ::v-deep .v-stepper__content .v-card .v-select--outlined fieldset,
    ::v-deep .v-stepper__content .v-card .v-textarea--outlined fieldset,
    ::v-deep .v-stepper__content .card .v-text-field--outlined fieldset,
    ::v-deep .v-stepper__content .card .v-select--outlined fieldset,
    ::v-deep .v-stepper__content .card .v-textarea--outlined fieldset,
    ::v-deep .v-stepper__content .form-card .v-text-field--outlined fieldset,
    ::v-deep .v-stepper__content .form-card .v-select--outlined fieldset,
    ::v-deep .v-stepper__content .form-card .v-textarea--outlined fieldset,
    ::v-deep .v-stepper__content .content-card .v-text-field--outlined fieldset,
    ::v-deep .v-stepper__content .content-card .v-select--outlined fieldset,
    ::v-deep .v-stepper__content .content-card .v-textarea--outlined fieldset {
      border-color: #e5e7eb !important;
      box-shadow: 0 1px 1px rgba(0, 0, 0, 0.25) !important; // Subtle crisp elevation
    }

    // Style selected menu items to match primary button color
    ::v-deep .v-stepper__content .v-select .v-menu__content .v-list-item--active,
    ::v-deep .v-stepper__content .v-select .v-menu__content .v-list-item--active .v-list-item__title,
    ::v-deep .v-stepper__content .v-select .v-menu__content .v-list-item:hover {
      background-color: #406A94 !important;
      color: #ffffff !important;
    }

    // Also target global dropdown menus when opened from cards in stepper
    ::v-deep .v-menu__content .v-list-item--active {
      background-color: #406A94 !important;
      color: #ffffff !important;
    }

    // Change tooltip icons to outline style instead of filled
    ::v-deep .v-stepper__content .v-icon.mdi-information:before {
      content: "\F02FD" !important; // mdi-information-outline
    }

    ::v-deep .v-stepper__content .v-icon.mdi-help:before {
      content: "\F0625" !important; // mdi-help-circle-outline
    }

    ::v-deep .v-stepper__content .v-icon.mdi-help-circle:before {
      content: "\F0625" !important; // mdi-help-circle-outline
    }

    // Ensure JSON editor stretches vertically within stepper
    ::v-deep .v-stepper__content .json-editor {
      height: 100% !important;
      min-height: 400px !important;
      display: flex !important;
      flex-direction: column !important;
    }

    ::v-deep .v-stepper__content .json-editor__container {
      flex: 1 !important;
      height: 100% !important;
      display: flex !important;
      flex-direction: column !important;
    }

    // Ensure parent containers also stretch properly
    ::v-deep .v-stepper__content .v-card .json-editor,
    ::v-deep .v-stepper__content .card .json-editor,
    ::v-deep .v-stepper__content .form-card .json-editor,
    ::v-deep .v-stepper__content .content-card .json-editor {
      height: 100% !important;
      min-height: 400px !important;
    }

    // Make sure card containers support full height
    ::v-deep .v-stepper__content .v-card,
    ::v-deep .v-stepper__content .card,
    ::v-deep .v-stepper__content .form-card,
    ::v-deep .v-stepper__content .content-card {
      display: flex !important;
      flex-direction: column !important;
    }

    ::v-deep .v-stepper__content .v-card__text,
    ::v-deep .v-stepper__content .v-card__actions {
      flex: 1 !important;
      display: flex !important;
      flex-direction: column !important;
    }

    // CodeMirror custom styling for field tokens within stepper
    ::v-deep .v-stepper__content .json-editor .CodeMirror {
      height: 100% !important;
      flex: 1 !important;

      .cm-string {
        color: #406A94 !important;
      }

      .cm-property {
        color: #1565c0 !important;
        font-weight: 600 !important;
      }

      .cm-number {
        color: #406A94 !important;
        font-weight: 500 !important;
      }

      .cm-atom {
        color: #f57c00 !important;
        font-weight: 500 !important;
      }

      .cm-punctuation,
      .cm-bracket {
        color: #5e35b1 !important;
        font-weight: 500 !important;
      }

      .cm-keyword {
        color: #d32f2f !important;
        font-weight: 500 !important;
      }

      .cm-variable {
        color: #424242 !important;
      }

      .cm-comment {
        color: #757575 !important;
        font-style: italic !important;
      }

      .CodeMirror-cursor {
        border-left: 2px solid #406A94 !important;
      }

      &.CodeMirror-focused .CodeMirror-cursor {
        border-left: 2px solid #406A94 !important;
      }
    }

    // Also target other common card/form container classes
    ::v-deep .v-stepper__content .card,
    ::v-deep .v-stepper__content .form-card,
    ::v-deep .v-stepper__content .content-card {
      background: rgb(240,241,242) !important;
      border: 1px solid #e5e7eb !important; // Match step border color
      border-radius: var(--radius-lg) !important; // Increased radius for cards
      box-shadow: none !important; // Remove drop shadow
    }
  }


  // Step label styling
  &__step-label {
    text-align: left;
    margin-left: 16px;
    flex: 1;
  }

  // Step check icon styling
  &__step-check {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 16px;
    flex-shrink: 0;
  }

  &__step-title {
    font-size: 1rem;
    font-weight: 600;
    color: #3C3C3C;
  }

  &__step-subtitle {
    font-size: 14px;
    color: #6b7884;
  }

  &__disabled-reason {
    font-size: 12px;
    color: #757575;
    margin-top: 4px;
  }

  // Content styling
  &__step-content {
    padding: 0 !important;
  }

  &__default-content {
    color: #525252;
    line-height: 1.5;
    margin-bottom: 16px;
  }


  // Main actions (bottom Previous/Next) - pinned to bottom
  &__actions {
    position: fixed;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    justify-content: space-between;
    padding: 16px 24px;
    background: #ffffff;
    border-top: 1px solid #f0f2f4;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
    z-index: 1000;

    // Responsive widths to match the stepper content (full width on all breakpoints except xl)
    width: 100%;
    @media (min-width: 1904px) { // xl and up
      width: 66.666667%; // 8/12 columns to match stepper
    }
  }

  &__action-btn {
    // Match admin portal button padding (same as SecondaryActionButton/PrimaryActionButton)
    padding-left: 40px !important;  // px-10 from admin portal
    padding-right: 40px !important; // px-10 from admin portal
    padding-top: 8px !important;    // Consistent vertical padding
    padding-bottom: 8px !important; // Consistent vertical padding
    height: auto !important;
    min-height: 36px !important;

    &--primary {
      background: #406A94 !important;
      color: #ffffff !important;
    }
  }
}

// Responsive design
@media (max-width: 600px) {
  .stepper-accordion-v {
    &__container {
      padding: 12px;
      padding-bottom: 20px; // Further reduced padding for mobile
    }


    &__stepper ::v-deep .v-stepper__step__step {
      width: 28px !important;
      height: 28px !important;
      min-width: 28px !important;
      min-height: 28px !important;
      border-radius: 50% !important;
      font-size: 14px !important;
    }

    &__stepper ::v-deep .v-stepper__content {
      padding: 16px !important;
    }

    &__step-title {
      font-size: 15px;
    }

    &__step-subtitle {
      font-size: 13px;
    }

    &__actions {
      padding: 12px 16px; // Reduced padding for mobile
    }
  }
}
</style>

<!-- Unscoped styles for dark mode support -->
<style lang="scss">
/* Dark mode overrides for StepperAccordion */
.dark,
.theme--dark {
  .stepper-accordion-v {
    background: var(--global-main-background-color, #121214) !important;
  }

  .stepper-accordion-v__title {
    color: var(--global-foreground, #f5f5f5) !important;
  }

  .stepper-accordion-v__step-title {
    color: var(--global-foreground, #f5f5f5) !important;
  }

  .stepper-accordion-v__step-subtitle {
    color: var(--global-muted-foreground, #9ca3af) !important;
  }

  .stepper-accordion-v__disabled-reason {
    color: var(--global-muted-foreground, #6b7280) !important;
  }

  .stepper-accordion-v__default-content {
    color: var(--global-muted-foreground, #9ca3af) !important;
  }

  /* Stepper step headers */
  .stepper-accordion-v__stepper .v-stepper__step {
    background: var(--global-card-background, #1c1c1e) !important;
    border-color: var(--global-border-color, #2e2e32) !important;
  }

  .stepper-accordion-v__stepper .v-stepper__step:hover {
    background: var(--global-card-background, #1c1c1e) !important;
    border-color: var(--global-border-color, #2e2e32) !important;
  }

  .stepper-accordion-v__stepper .v-stepper__step--active {
    border-color: var(--global-border-color, #2e2e32) !important;
  }

  .stepper-accordion-v__stepper .v-stepper__step--active + .v-stepper__content {
    border-color: var(--global-border-color, #2e2e32) !important;
  }

  /* Step content */
  .stepper-accordion-v__stepper .v-stepper__content {
    background: var(--global-card-background, #1c1c1e) !important;
    border-color: var(--global-border-color, #2e2e32) !important;
  }

  /* Cards within steps */
  .stepper-accordion-v__stepper .v-stepper__content .v-card,
  .stepper-accordion-v__stepper .v-stepper__content .card,
  .stepper-accordion-v__stepper .v-stepper__content .form-card,
  .stepper-accordion-v__stepper .v-stepper__content .content-card {
    background: var(--global-card-background, #1c1c1e) !important;
    border-color: var(--global-border-color, #2e2e32) !important;
  }

  /* Text fields in cards */
  .stepper-accordion-v__stepper .v-stepper__content .v-text-field .v-input__control .v-input__slot,
  .stepper-accordion-v__stepper .v-stepper__content .v-select .v-input__control .v-input__slot,
  .stepper-accordion-v__stepper .v-stepper__content .v-textarea .v-input__control .v-input__slot {
    background: var(--global-card-background, #1c1c1e) !important;
    border-color: var(--global-border-color, #2e2e32) !important;
  }

  .stepper-accordion-v__stepper .v-stepper__content .v-text-field--outlined fieldset,
  .stepper-accordion-v__stepper .v-stepper__content .v-select--outlined fieldset,
  .stepper-accordion-v__stepper .v-stepper__content .v-textarea--outlined fieldset {
    border-color: var(--global-border-color, #2e2e32) !important;
  }

  /* Step numbers - use primary colour in dark mode */
  .stepper-accordion-v__stepper .v-stepper__step__step {
    background: var(--global-primary, #ed303e) !important;
  }

  .stepper-accordion-v__stepper .v-stepper__step--active .v-stepper__step__step {
    background: var(--global-primary, #ed303e) !important;
  }

  .stepper-accordion-v__stepper .v-stepper__step:hover .v-stepper__step__step,
  .stepper-accordion-v__stepper .v-stepper__step--active:hover .v-stepper__step__step {
    background: var(--global-primary, #ed303e) !important;
  }

  /* Actions bar */
  .stepper-accordion-v__actions {
    background: var(--global-card-background, #1c1c1e) !important;
    border-top-color: var(--global-border-color, #2e2e32) !important;
  }

  .stepper-accordion-v__action-btn--primary {
    background: var(--global-primary, #ed303e) !important;
  }
}
</style>
